import React, { useState, useEffect } from 'react';
import { getWeatherIconFixed, convertToLocationTime } from './weatherUtils.js';
import { transformOWMData } from './apiService.js';
import HourlyChart from './HourlyChart.js';
import DailyForecast from './DailyForecast.js';
import Current from './Current.js';

// Weather icon function - simplified for OWM (no more Foreca symbol codes)
// OWM gives us clean condition text + cloudiness percentage directly
const getWeatherIconFixedLocal = (conditionText, isDay, cloudinessPercent = null) => {
  if (!conditionText) return isDay === 1 ? '/sunny.png' : '/clear.png';
  
  const normalizedCondition = conditionText.toLowerCase();
  
  // Cloudiness overrides for clear/mainly clear conditions
  if (cloudinessPercent !== null) {
    if ((normalizedCondition === 'clear' || normalizedCondition.includes('sunny')) && 
        !normalizedCondition.includes('partly')) {
      if (cloudinessPercent >= 15) {
        return isDay === 1 ? '/partlycloudy.png' : '/cloudynight.png';
      } else if (cloudinessPercent >= 7) {
        return '/msunny.png';
      }
    }
    if (normalizedCondition.includes('mainly clear') || normalizedCondition.includes('mostly clear')) {
      if (cloudinessPercent >= 15) {
        return isDay === 1 ? '/partlycloudy.png' : '/cloudynight.png';
      }
    }
  }
  
  // Snow before rain (snow showers contain "shower")
  if (normalizedCondition.includes('snow') || normalizedCondition.includes('sleet')) {
    return '/snow.png';
  }
  
  // Mostly/mainly clear
  if (normalizedCondition.includes('mostly clear') || normalizedCondition.includes('mainly clear')) {
    return '/msunny.png';
  }
  
  if (normalizedCondition.includes('rain') || normalizedCondition.includes('drizzle') || normalizedCondition.includes('shower')) {
    if (normalizedCondition.includes('light')) return '/lightrain.png';
    if (normalizedCondition.includes('heavy')) return '/heavyrain.png';
    if (normalizedCondition.includes('freezing')) return '/sleet.png';
    return '/heavyrain.png';
  }
  
  if (normalizedCondition.includes('thunder')) {
    return '/thunderstorm.png';
  }
  
  if (normalizedCondition.includes('fog') || normalizedCondition.includes('mist')) {
    return '/fog.png';
  }
  
  if (normalizedCondition.includes('cloudy') || normalizedCondition.includes('overcast')) {
    if (normalizedCondition.includes('partly')) {
      return isDay === 1 ? '/partlycloudy.png' : '/cloudynight.png';
    }
    return isDay === 1 ? '/cloudy.png' : '/vcloudynight.png';
  }
  
  return isDay === 1 ? '/sunny.png' : '/clear.png';
};

// Function to get current cloudiness from hourly data
const getCurrentCloudiness = (weather) => {
  if (weather.hourlyMasterTable && weather.hourlyMasterTable.length > 0) {
    return weather.hourlyMasterTable[0].cloudiness || null;
  }
  return weather.current.cloudiness || null;
};

// Get corrected weather condition text based on cloudiness overrides
const getCorrectedConditionText = (conditionText, cloudinessPercent) => {
  if (!conditionText) return 'Unknown';
  
  const normalizedCondition = conditionText.toLowerCase();
  
  if (cloudinessPercent !== null) {
    if ((normalizedCondition === 'clear' || normalizedCondition.includes('sunny')) && 
        !normalizedCondition.includes('partly')) {
      if (cloudinessPercent >= 15) {
        return 'Partly Cloudy';
      } else if (cloudinessPercent >= 7) {
        return 'Mainly Clear';
      }
    }
    if (normalizedCondition.includes('mainly clear') || normalizedCondition.includes('mostly clear')) {
      if (cloudinessPercent >= 15) {
        return 'Partly Cloudy';
      }
    }
  }
  
  return conditionText;
};

const WeatherApp = () => {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [city, setCity] = useState('');
  const [showDetailedHourly, setShowDetailedHourly] = useState(false);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [recentLocations, setRecentLocations] = useState([]);

  // Load last searched city and recent locations on component mount
  useEffect(() => {
    try {
      // Check for shared weather report in URL
      const urlParams = new URLSearchParams(window.location.search);
      const sharedCity = urlParams.get('city');
      
      if (sharedCity) {
        setCity(sharedCity);
        fetchWeatherForCity(sharedCity);
        return;
      }
      
      // Load from localStorage if no shared city
      const lastSearchedCity = JSON.parse(localStorage.getItem('lastSearchedCity') || 'null');
      const savedRecentLocations = JSON.parse(localStorage.getItem('recentWeatherLocations') || '[]');
      
      if (savedRecentLocations) {
        setRecentLocations(savedRecentLocations);
      }
      
      if (lastSearchedCity) {
        setCity(lastSearchedCity);
        fetchWeatherForCity(lastSearchedCity);
      }
    } catch (error) {
      // Error loading saved data
    }
  }, []);

  // Save city to state storage and recent locations list
  const saveLastSearchedCity = (cityName) => {
    try {
      localStorage.setItem('lastSearchedCity', JSON.stringify(cityName));
      
      const currentRecent = JSON.parse(localStorage.getItem('recentWeatherLocations') || '[]');
      const filteredRecent = currentRecent.filter(location => location.toLowerCase() !== cityName.toLowerCase());
      const newRecentLocations = [cityName, ...filteredRecent].slice(0, 5);
      
      localStorage.setItem('recentWeatherLocations', JSON.stringify(newRecentLocations));
      setRecentLocations(newRecentLocations);
    } catch (error) {
      const filteredRecent = recentLocations.filter(location => location.toLowerCase() !== cityName.toLowerCase());
      const newRecentLocations = [cityName, ...filteredRecent].slice(0, 5);
      setRecentLocations(newRecentLocations);
    }
  };

  // Auto-scroll hourly chart to beginning when weather data changes
  useEffect(() => {
    if (weather && weather.hourlyMasterTable) {
      const chartElement = document.getElementById('hourlyChart');
      if (chartElement) {
        chartElement.scrollLeft = 0;
        setTimeout(() => { chartElement.scrollLeft = 0; }, 10);
        setTimeout(() => { chartElement.scrollLeft = 0; }, 50);
        setTimeout(() => { chartElement.scrollLeft = 0; }, 100);
      }
    }
  }, [weather]);

  // ============================================================
  // Fetch weather from OWM via scn.co proxy - SINGLE API CALL
  // ============================================================
  const fetchFromOWM = async (locationName) => {
    try {
      const response = await fetch(`https://scn.co/weather-proxy.php?location=${encodeURIComponent(locationName)}`);
      
      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }
      
      const rawText = await response.text();
      
      let data;
      try {
        data = JSON.parse(rawText);
      } catch (jsonError) {
        throw new Error(`Invalid JSON response: ${jsonError.message}`);
      }
      
      if (data.error) {
        throw new Error(data.error);
      }
      
      if (!data.location || !data.onecall) {
        throw new Error('Incomplete weather data received');
      }
      
      // Transform OWM data to match app structure
      const transformedData = transformOWMData(data);
      
      return transformedData;
    } catch (error) {
      throw new Error(`Weather API failed: ${error.message}`);
    }
  };

  // Function to properly capitalize weather conditions
  const formatWeatherCondition = (conditionText) => {
    return conditionText
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Main function to fetch weather
  const fetchWeatherForCity = async (cityName) => {
    setLoading(true);
    setError('');

    try {
      const weatherData = await fetchFromOWM(cityName);
      
      setWeather(weatherData);
      setError('');
      
      saveLastSearchedCity(cityName);
      updateBrowserUrl(cityName);
      
    } catch (error) {
      setError('Location not found. Please try a different city or check spelling.');
      setWeather(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchWeather = async () => {
    if (!city.trim()) {
      setError('Please enter a city name');
      return;
    }

    setShowLocationDropdown(false);
    await fetchWeatherForCity(city.trim());
  };

  // Function to get next 24 hours from master table
  const getNext24Hours = () => {
    if (!weather || !weather.hourlyMasterTable) return [];
    return weather.hourlyMasterTable.slice(0, 24);
  };

  // Function to update browser URL
  const updateBrowserUrl = (cityName) => {
    try {
      const url = new URL(window.location);
      url.searchParams.set('city', cityName);
      window.history.pushState({}, '', url);
    } catch (error) {
      // Error updating URL
    }
  };

  // Function to calculate rain chance in next 24 hours
  const getMaxRainChance = () => {
    const hourlyData = getNext24Hours();
    if (hourlyData.length === 0) return 0;
    
    const rainChances = hourlyData.map(hour => hour.chance_of_rain || 0);
    const maxRainChance = Math.max(...rainChances);
    
    if (maxRainChance === 0) return 0;
    
    let totalChance = maxRainChance;
    rainChances.forEach(chance => {
      if (chance > 0 && chance !== maxRainChance) {
        totalChance += chance / 24;
      }
    });
    
    return Math.min(99, Math.round(totalChance));
  };

  // Function to calculate snow chance in next 24 hours
  const getMaxSnowChance = () => {
    const hourlyData = getNext24Hours();
    if (hourlyData.length === 0) return 0;
    
    const snowChances = hourlyData.map(hour => hour.chance_of_snow || 0);
    const maxSnowChance = Math.max(...snowChances);
    
    if (maxSnowChance === 0) return 0;
    
    let totalChance = maxSnowChance;
    snowChances.forEach(chance => {
      if (chance > 0 && chance !== maxSnowChance) {
        totalChance += chance / 24;
      }
    });
    
    return Math.min(99, Math.round(totalChance));
  };

  const WeatherIcon = () => (
    <div style={{textAlign: 'center', marginBottom: '2rem'}}>
      {weather ? (
        <img 
          src={getWeatherIconFixedLocal(
            weather.current.condition.text, 
            weather.current.is_day,
            getCurrentCloudiness(weather)
          )}
          alt={getCorrectedConditionText(weather.current.condition.text, getCurrentCloudiness(weather))}
          style={{width: '12rem', height: '12rem', margin: '0 auto', objectFit: 'contain'}}
        />
      ) : (
        <img 
          src="/weather.png"
          alt="Weather Icon"
          style={{width: '12rem', height: '12rem', margin: '0 auto', objectFit: 'contain'}}
        />
      )}
    </div>
  );

  // Simple detailed hourly view
  if (showDetailedHourly) {
    return (
      <div style={{minHeight: '100vh', background: 'black', color: 'white', padding: '1rem'}}>
        <div style={{maxWidth: '1500px', margin: '0 auto'}}>
          <button 
            onClick={() => setShowDetailedHourly(false)}
            style={{
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              padding: '0.75rem 1.5rem',
              marginBottom: '1rem',
              cursor: 'pointer'
            }}
          >
            ← Back to Main
          </button>
          
          <h2 style={{fontSize: '1.5rem', marginBottom: '1rem'}}>24-Hour Detailed Forecast</h2>
          
          {weather && weather.hourlyMasterTable && (
            <div style={{display: 'grid', gap: '0.5rem'}}>
              {weather.hourlyMasterTable.slice(0, 24).map((hour, index) => {
                const time = convertToLocationTime(hour.time, weather?.location?.timezone);
                const windSpeedMph = hour.wind_speed || 0;
                
                return (
                  <div key={index} style={{
                    background: '#1f2937',
                    borderRadius: '0.5rem',
                    padding: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem'
                  }}>
                    <div style={{minWidth: '80px', fontWeight: 'bold'}}>{time}</div>
                    
                    <img 
                      src={getWeatherIconFixedLocal(
                        hour.condition?.text, 
                        hour.is_day,
                        hour.cloudiness
                      )}
                      alt={getCorrectedConditionText(
                        hour.condition?.text || 'Weather condition',
                        hour.cloudiness
                      )}
                      style={{width: '40px', height: '40px'}}
                    />
                    
                    <div style={{flex: 1}}>
                      <div>
                        {formatWeatherCondition(
                          getCorrectedConditionText(
                            hour.condition?.text || 'Weather condition',
                            hour.cloudiness
                          )
                        )}
                      </div>
                      <div style={{fontSize: '0.8rem', color: '#9ca3af'}}>
                        Rain: {hour.chance_of_rain}% | Wind: {Math.round(windSpeedMph)} mph
                      </div>
                    </div>
                    
                    <div style={{textAlign: 'right', minWidth: '120px'}}>
                      <div style={{fontSize: '1.1rem', fontWeight: 'bold'}}>
                        {hour.temp_f}°F / {hour.temp_c}°C
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{minHeight: '100vh', background: 'black', color: 'white', padding: '1rem', width: '100%', overflowX: 'hidden', boxSizing: 'border-box'}}>
      <style>
        {`
          body, html {
            overflow-x: hidden;
            max-width: 100vw;
            margin: 0;
            padding: 0;
          }
          
          * {
            box-sizing: border-box;
          }
          
          input::placeholder {
            color: rgba(255, 255, 255, 0.7) !important;
          }
          
          #hourlyChart::-webkit-scrollbar {
            display: none;
          }
        `}
      </style>
      
      <div style={{maxWidth: '1500px', width: '100%', margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0', boxSizing: 'border-box'}}>
        <WeatherIcon />

        <div style={{
          background: '#00395b', 
          borderRadius: '1rem', 
          padding: '0.75rem', 
          marginBottom: '1.5rem',
          width: '100%',
          maxWidth: '400px',
          boxSizing: 'border-box',
          position: 'relative'
        }}>
          <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  fetchWeather();
                }
              }}
              onFocus={() => setShowLocationDropdown(true)}
              onBlur={() => {
                setTimeout(() => setShowLocationDropdown(false), 150);
              }}
              placeholder="Location, Zip or Lat, Lon ?"
              style={{
                flex: 1,
                padding: '0.75rem 1rem',
                background: 'transparent',
                color: 'white',
                border: 'none',
                outline: 'none',
                fontSize: '16px'
              }}
            />
            <button
              onClick={fetchWeather}
              disabled={loading}
              style={{
                background: 'transparent',
                color: 'white',
                border: 'none',
                cursor: 'pointer',
                padding: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {loading ? '...' : (
                <svg style={{width: '20px', height: '20px'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              )}
            </button>
          </div>
          
          {/* Recent Locations Dropdown */}
          {showLocationDropdown && recentLocations.length > 0 && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: '0.75rem',
              right: '0.75rem',
              background: '#1f2937',
              borderRadius: '0.5rem',
              marginTop: '0.25rem',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
              zIndex: 1000,
              maxHeight: '200px',
              overflowY: 'auto'
            }}>
              <div style={{
                padding: '0.5rem 0.75rem',
                fontSize: '0.8rem',
                color: '#9ca3af',
                borderBottom: '1px solid #374151'
              }}>
                Recent Locations
              </div>
              {recentLocations.map((location, index) => (
                <div
                  key={index}
                  onMouseDown={() => {
                    setCity(location);
                    setShowLocationDropdown(false);
                    setTimeout(() => fetchWeatherForCity(location), 100);
                  }}
                  style={{
                    padding: '0.75rem',
                    cursor: 'pointer',
                    borderBottom: index < recentLocations.length - 1 ? '1px solid #374151' : 'none',
                    color: 'white',
                    fontSize: '0.9rem'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#374151';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'transparent';
                  }}
                >
                  {location}
                </div>
              ))}
            </div>
          )}
        </div>

        {error && (
          <div style={{
            background: '#dc2626',
            color: 'white',
            padding: '0.75rem',
            borderRadius: '0.75rem',
            marginBottom: '1.5rem',
            textAlign: 'center',
            width: '100%',
            maxWidth: '400px',
            boxSizing: 'border-box'
          }}>
            {error}
          </div>
        )}

        {weather && !loading && (
          <div className="weather-grid" style={{
            background: 'black',
            borderRadius: '1rem',
            padding: '1rem',
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            display: 'block'
          }}>
            {/* Current Weather Section */}
            <Current 
              weather={weather} 
              getMaxRainChance={getMaxRainChance}
              getMaxSnowChance={getMaxSnowChance}
              formatWeatherCondition={formatWeatherCondition}
            />

            {/* Hourly Info Chart */}
            <HourlyChart weather={weather} />

            {/* Daily Forecast */}
            <DailyForecast weather={weather} />
          </div>
        )}
      </div>
    </div>
  );
};

export default WeatherApp;
